import {
  WSButton,
  WSInfoBox,
  WSText,
  WSTimelineItemProps
} from "@wingspanhq/fe-component-library";
import { Batch, BatchStatus } from "@wingspanhq/wingspan-api-sdk";
import { formatNumber } from "accounting";

import {
  selectorImportBatchTimelineEvents,
  ImportBatchEventType
} from "../../selectors/selectorImportBatchTimelineEvents";
import { selectorImportBatchUploadedFilename } from "../../selectors/selectorImportBatchUploadedFilename";
import { selectorIsImportBatchFullyImported } from "../../selectors/selectorIsImportBatchFullyImported";
import { selectorIsImportBatchPartiallyImported } from "../../selectors/selectorIsImportBatchPartiallyImported";

export type ImportBatchTimelineArgs = {
  batch: Batch;
  /** `BatchSummary.progress.total`. */
  itemCount?: number;
  /** `BatchSummary.progress.completed`. */
  completedCount?: number;
  /** `BatchSummary.progress.failed`. */
  failedCount?: number;
  /** Singular noun for what the batch imports, e.g. "Payable". */
  entityLabel: string;
  /** Plural, title-case noun, e.g. "Payables". */
  entityLabelPlural: string;
  /** The full history page spells every event out; the widget links to it. */
  showDetailedContent?: boolean;
  onSeeMore: () => void;
};

const SeeMoreButton: React.FC<{ onClick: () => void; label?: string }> = ({
  onClick,
  label = "see more"
}) => <WSButton.Link onClick={onClick}>{label}</WSButton.Link>;

const getImportCompletedContent = (args: ImportBatchTimelineArgs) => {
  const { batch, entityLabelPlural, failedCount } = args;
  const formattedProcessed = formatNumber(args.completedCount || 0);
  const formattedFailed = formatNumber(failedCount || 0);
  const formattedTotal = formatNumber(args.itemCount || 0);

  const outcome = selectorIsImportBatchFullyImported({ batch, failedCount })
    ? {
        title: `${entityLabelPlural} imported`,
        bullets: [`${formattedProcessed} of ${formattedTotal} imported`]
      }
    : selectorIsImportBatchPartiallyImported({ batch, failedCount })
      ? {
          title: `${entityLabelPlural} imported partially`,
          bullets: [
            `${formattedProcessed} of ${formattedTotal} imported`,
            `${formattedFailed} of ${formattedTotal} failed`
          ]
        }
      : batch.status === BatchStatus.FAILED
        ? {
            title: `${entityLabelPlural} import failed`,
            bullets: [`${formattedFailed} of ${formattedTotal} failed`]
          }
        : undefined;

  if (!outcome) {
    return null;
  }

  return args.showDetailedContent ? (
    <>
      <WSText.ParagraphSm>{outcome.title}</WSText.ParagraphSm>
      <WSInfoBox>
        <ul>
          {outcome.bullets.map(bullet => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      </WSInfoBox>
    </>
  ) : (
    <>
      <WSText.ParagraphSm inline>{outcome.title}</WSText.ParagraphSm>
      <SeeMoreButton onClick={args.onSeeMore} />
    </>
  );
};

const getContent = (
  eventType: ImportBatchEventType,
  args: ImportBatchTimelineArgs
): WSTimelineItemProps["content"] => {
  const filename = selectorImportBatchUploadedFilename({
    batch: args.batch,
    entityLabel: args.entityLabel
  });

  switch (eventType) {
    case ImportBatchEventType.BatchCreated:
      return args.showDetailedContent ? (
        <WSText.ParagraphSm>Batch created</WSText.ParagraphSm>
      ) : (
        <WSText.ParagraphSm inline>
          Batch created <SeeMoreButton onClick={args.onSeeMore} />
        </WSText.ParagraphSm>
      );

    case ImportBatchEventType.UploadCompleted:
      return args.showDetailedContent ? (
        <>
          <WSText.ParagraphSm mb="S">
            Spreadsheet upload completed
          </WSText.ParagraphSm>
          <WSInfoBox>
            <ul>
              <li>{filename} is uploaded.</li>
              <li>Upload completed</li>
            </ul>
          </WSInfoBox>
        </>
      ) : (
        <WSText.ParagraphSm inline>
          Upload completed <SeeMoreButton onClick={args.onSeeMore} />
        </WSText.ParagraphSm>
      );

    case ImportBatchEventType.ImportStarted:
      return args.showDetailedContent ? (
        <>
          <WSText.ParagraphSm mb="S">
            {args.entityLabelPlural} import started
          </WSText.ParagraphSm>
          <WSInfoBox>
            <ul>
              <li>{filename} is uploaded</li>
            </ul>
          </WSInfoBox>
        </>
      ) : (
        <WSText.ParagraphSm inline>
          Import started <SeeMoreButton onClick={args.onSeeMore} />
        </WSText.ParagraphSm>
      );

    case ImportBatchEventType.ImportCompleted:
      return getImportCompletedContent(args);

    default:
      return null;
  }
};

/**
 * Timeline entries for an import batch.
 *
 * v1 stamped every entry with the team member who triggered it; v3 records
 * milestones as bare timestamps, so entries carry a neutral marker instead of
 * an actor avatar and badge.
 */
export const getImportBatchTimelineItems = (
  args: ImportBatchTimelineArgs
): WSTimelineItemProps[] =>
  selectorImportBatchTimelineEvents(args.batch).map(event => ({
    date: event.timestamp,
    content: getContent(event.eventType, args),
    avatar: { type: "icon", icon: "info-circle" }
  }));
